import React, { useCallback, useEffect, useState } from 'react';
import { RefreshIcon } from '@heroicons/react/outline';
import { apiFetchReferralDocument, apiListReferralDocuments } from '../../utils/rmsApi';
import {
  OVERVIEW_CARD_LIMIT,
  UPLOADS_EMPTY_BEFORE_SEND,
  UPLOADS_EMPTY_SENT,
  UPLOADS_PDF_FAILED,
  UPLOADS_VIEW_ALL,
  canRetryPdf,
  isReferralSent,
} from '../../utils/referralUploads';
import useConfidentialDownload from '../../hooks/useConfidentialDownload';
import ServiceUserUploadRow from './ServiceUserUploadRow';

/**
 * ServiceUserUploadsPanel
 * -----------------------
 * The documents a school or a family attached to a referral (TDI-602), shown
 * inside the Triage Application. Two shapes, one component and one row, so
 * the card and the panel can never describe the same document differently:
 *
 *   variant="card"  the patient overview, directly under Rating scales, in
 *                   the overview card style. The first five, then "View all
 *                   on Documents" (FR1, TDI-611). View only.
 *   variant="panel" the Documents page, after the questionnaires and laid out
 *                   like them: every document, each with its description,
 *                   type, date covered, provider and uploaded date (FR2,
 *                   TDI-610). View and PDF.
 *
 * Rows arrive in the server's order: school first, then parent or carer,
 * oldest first. View opens the PDF copy inline in a new tab, exactly as the
 * outcome letter's View does, with no warning (FR3). Download is offered only
 * to the roles the server allows (Tom King, 31 October 2026: support users
 * and non-clinical org admins for the pilot; clinicians view), and passes
 * through the confidentiality warning on the user's first download for this
 * patient (FR12).
 *
 * Read-only by design (FR6): nothing here changes a file or its labels.
 */
export default function ServiceUserUploadsPanel({
  orgId,
  patientRecordId,
  patientRecord = null,
  variant = 'panel',
  onSeeAll,
}) {
  const [documents, setDocuments] = useState(null);
  const [canDownload, setCanDownload] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const { requestDownload, confirmDialog } = useConfidentialDownload({
    orgId, patientRecordId, patientRecord,
  });

  const reload = useCallback(async () => {
    const res = await apiListReferralDocuments({ orgId, patientRecordId });
    setDocuments(Array.isArray(res?.documents) ? res.documents : []);
    setCanDownload(Boolean(res?.canDownload));
  }, [orgId, patientRecordId]);

  useEffect(() => {
    let cancelled = false;
    if (!orgId || !patientRecordId) return undefined;
    (async () => {
      try {
        const res = await apiListReferralDocuments({ orgId, patientRecordId });
        if (cancelled) return;
        setDocuments(Array.isArray(res?.documents) ? res.documents : []);
        setCanDownload(Boolean(res?.canDownload));
      } catch (err) {
        if (!cancelled) setError(err?.message || 'The documents could not be loaded. Refresh the page to try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, patientRecordId]);

  const open = useCallback(async (row, disposition) => {
    if (busyId) return;
    setBusyId(row.id);
    setError(null);
    try {
      const res = await apiFetchReferralDocument({
        orgId, patientRecordId, documentId: row.id, disposition,
      });
      if (!res?.downloadUrl) {
        setError('The document could not be opened. Please try again.');
      } else if (disposition === 'attachment') {
        // An attachment URL downloads without leaving the page, and is not
        // caught by pop-up blockers the way a new tab after an await is.
        window.location.assign(res.downloadUrl);
      } else {
        window.open(res.downloadUrl, '_blank', 'noopener');
      }
    } catch (err) {
      setError(err?.message || 'The document could not be opened. Please try again.');
    } finally {
      setBusyId(null);
    }
  }, [orgId, patientRecordId, busyId]);

  const isCard = variant === 'card';
  const loading = documents === null && !error;
  const rows = documents || [];
  const shown = isCard ? rows.slice(0, OVERVIEW_CARD_LIMIT) : rows;
  const hasMore = rows.length > shown.length;
  const emptyLine = isReferralSent(patientRecord) ? UPLOADS_EMPTY_SENT : UPLOADS_EMPTY_BEFORE_SEND;

  const onView = (row) => open(row, 'inline');
  const onDownload = !isCard && canDownload
    ? (row) => requestDownload(() => open(row, 'attachment'))
    : undefined;
  // A Word upload whose PDF copy failed stayed "Preparing PDF" for good
  // (TDI-762): the fetch converts a failed copy again on request, but nothing
  // ever called it for a row without a copy. "Try again" calls it; on success
  // the list reloads so the row shows View and download, and a copy that
  // still fails tells staff plainly.
  const onRetry = async (row) => {
    if (busyId) return;
    setBusyId(row.id);
    setError(null);
    try {
      await apiFetchReferralDocument({
        orgId, patientRecordId, documentId: row.id, disposition: 'inline',
      });
      await reload();
    } catch (err) {
      const stillNotReady = String(err?.code || '').includes('failed-precondition')
        || err?.details?.reason === 'pdf_not_ready';
      setError(stillNotReady
        ? UPLOADS_PDF_FAILED
        : (err?.message || 'The document could not be opened. Please try again.'));
    } finally {
      setBusyId(null);
    }
  };

  let body;
  if (loading) {
    body = (
      <p className="inline-flex items-center gap-2 text-sm italic text-gray-600">
        <RefreshIcon className="w-4 h-4 animate-spin" aria-hidden="true" />
        Loading documents…
      </p>
    );
  } else if (rows.length === 0 && !error) {
    body = <p className="text-sm text-gray-600">{emptyLine}</p>;
  } else {
    body = (
      <ul className={isCard ? 'space-y-2' : 'relative'}>
        {!isCard && rows.length > 1 && (
          <li aria-hidden="true" className="absolute left-[40px] top-6 bottom-6 w-0.5 bg-gray-200" />
        )}
        {shown.map((row, index) => (
          <ServiceUserUploadRow
            key={row.id}
            row={row}
            index={index}
            variant={variant}
            onView={onView}
            onDownload={onDownload}
            onRetry={canRetryPdf(row) ? onRetry : undefined}
            busy={busyId === row.id}
          />
        ))}
      </ul>
    );
  }

  const errorAlert = error && (
    <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 text-left">
      {error}
    </div>
  );

  if (isCard) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100 text-center">
          <h3 className="text-lg font-bold text-gray-900">Service User Uploads</h3>
        </div>
        <div className="px-5 py-4 text-left">
          {errorAlert}
          {body}
          {hasMore && (
            <button
              type="button"
              onClick={onSeeAll}
              className="mt-3 min-h-[44px] text-sm font-semibold text-primary underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {UPLOADS_VIEW_ALL}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl p-8 mx-auto mb-4 text-left bg-white rounded-xl shadow-sm border">
      <div className="ml-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Service User Uploads</h1>
        <p className="text-sm text-gray-600 mt-1">
          Documents the school or the parent or carer added to this referral.
        </p>
      </div>
      <div className="ml-4">{errorAlert}</div>
      <div className={loading || rows.length === 0 ? 'ml-4' : ''}>{body}</div>
      {confirmDialog}
    </div>
  );
}
